"use client";

import Link from "next/link";
import "./professional-services.css";

const valueChain = [
  {
    number: "01",
    title: "MARKET & SELL",
    text: "Leads & CRM | Opportunities | Proposals | Pipeline",
    type: "market",
    image: "/assets/images/MARKET-&-SELL.jpg",
  },
  {
    number: "02",
    title: "CONTRACT & SOW",
    text: "SOW / MSA | Rate Card | COLA Terms | Commercials",
    type: "contract",
    image: "/assets/images/CONTRACT-&-SOW.jpg",
  },
  {
    number: "03",
    title: "PEOPLE & SKILLS",
    text: "HCM / HR | Skills & Certifications | Availability | Staffing & Allocation",
    type: "people",
    image: "/assets/images/PEOPLE-&-SKILLS.jpg",
  },
  {
    number: "04",
    title: "DELIVER & TRACK",
    text: "Projects / WBS | Timesheets | Milestones | Quality & Delivery",
    type: "delivery",
    image: "/assets/images/DELIVER-&-TRACK.jpg",
  },
  {
    number: "05",
    title: "BILL & RECOGNISE",
    text: "Billing & Invoicing | Revenue Recognition | Unbilled Work | Expense Recovery",
    type: "billing",
    image: "/assets/images/BILL-&-RECOGNISE.jpg",
  },
  {
    number: "06",
    title: "COLLECT & CASH",
    text: "Collections | DSO | Aged Receivables | Cash & Working Capital",
    type: "cash",
    image: "/assets/images/COLLECT-&-CASH.jpg",
  },
];

const peopleToMoney = [
  {
    title: "HCM",
    subtitle: "People & Skills",
    points: [
      "Employee",
      "Grade & Cost",
      "Skills",
      "Availability",
      "Increment / COLA",
      "Utilisation",
    ],
    type: "people",
  },
  {
    title: "PROFESSIONAL SERVICES OPERATIONS",
    subtitle: "",
    points: [
      "Projects / WBS",
      "Timesheets",
      "Milestones",
      "Expenses",
      "Subcontractors",
      "Delivery & Acceptance",
    ],
    type: "delivery",
  },
  {
    title: "REVENUE & P&L",
    subtitle: "",
    points: [
      "Billing",
      "Revenue",
      "Project Margin",
      "Gross Margin",
      "P&L",
      "Forecast",
    ],
    type: "revenue",
  },
  {
    title: "CASH & VALUE",
    subtitle: "",
    points: [
      "Collections",
      "DSO",
      "Working Capital",
      "Cash Flow",
      "Business Value",
      "Growth",
    ],
    type: "cash",
  },
];

const colaQuestions = [
  "Track COLA terms across contracts",
  "Identify upcoming COLA anniversaries",
  "Model impact of salary increments on margin",
  "Alert account owners before margin erodes",
  "Simulate rate revision scenarios",
  "Automate COLA workflow and approvals",
];

const aiUseCases = [
  {
    title: "Pipeline & Win Intelligence",
    text: "Which deals to focus on? Win probability and effort.",
    type: "market",
  },
  {
    title: "Staffing & Skill Intelligence",
    text: "Right people, right skill, right time.",
    type: "people",
  },
  {
    title: "Project Health & Delivery Risk",
    text: "Detect slippage, predict delays, recommend actions.",
    type: "delivery",
  },
  {
    title: "Revenue & Margin Intelligence",
    text: "Busy but unprofitable projects, margin leakage detection.",
    type: "revenue",
  },
  {
    title: "COLA & Cost Intelligence",
    text: "Impact increments, COLA and cost changes.",
    type: "cost",
  },
  {
    title: "Billing & Collections Intelligence",
    text: "Unbilled work, overdue invoices, cash forecasting.",
    type: "billing",
  },
  {
    title: "Capacity & Workforce Planning",
    text: "Future skills and resource demand.",
    type: "people",
  },
];

const busyQuestions = [
  "Which projects are busy but unprofitable?",
  "Which contracts have COLA due?",
  "Where are we delivering work that hasn't been billed?",
  "Which milestones are complete but invoices not raised?",
  "Which invoices are overdue?",
  "Which expensive resources are doing lower-grade work?",
  "Where is bench capacity building?",
  "What skills will we need in the next 90 days?",
  "Which projects are likely to miss margin?",
  "What should the Partner act on this week?",
];

const whatsHappeningRows = [
  { label: "Headcount", icon: "people", arrow: "↑", trend: "blue" },
  { label: "People Cost", icon: "cost", arrow: "↑", trend: "blue" },
  { label: "Utilisation", icon: "utilisation", arrow: "↓", trend: "red" },
  { label: "Revenue", icon: "revenue", arrow: "→", trend: "blue" },
  { label: "COLA Applied", icon: "contract", arrow: "×", trend: "red" },
  { label: "Unbilled Work", icon: "billing", arrow: "↑", trend: "blue" },
  { label: "DSO", icon: "cash", arrow: "↑", trend: "blue" },
];

const resultRows = [
  { label: "Revenue", icon: "revenue", arrow: "↑", trend: "blue" },
  { label: "Costs", icon: "cost", arrow: "↑↑", trend: "red" },
  { label: "Margin", icon: "margin", arrow: "↓", trend: "red" },
  { label: "P&L", icon: "business", arrow: "↓", trend: "red" },
  { label: "Cash", icon: "cash", arrow: "↓", trend: "red" },
];

const systems = [
  ["SAP", "S/4HANA", "enterprise"],
  ["SuccessFactors", "People", "people"],
  ["Salesforce", "CRM", "crm"],
  ["Microsoft Dynamics 365", "Business", "business"],
  ["Jira", "Projects", "project"],
  ["Concur", "Expenses", "expense"],
  ["BTP", "Integration", "integration"],
  ["Other Systems", "Enterprise Data", "data"],
];

const outcomes = [
  {
    value: "10–20%",
    label: "Higher Utilisation",
    type: "utilisation",
    image: "/assets/images/Higher-Utilisation.jpg",
  },
  {
    value: "3–8%",
    label: "Higher Margins",
    type: "margin",
    image: "/assets/images/Higher-Margins.jpg",
  },
  {
    value: "20–40%",
    label: "Faster Collections",
    type: "cash",
    image: "/assets/images/Faster-Collections.jpg",
  },
  {
    value: "Better",
    label: "Workforce Planning",
    type: "people",
    image: "/assets/images/Better-Workforce-Planning.jpg",
  },
];

const heroStats = [
  ["utilisation", "Win More", "Business"],
  ["people", "Better", "Utilisation"],
  ["margin", "Higher", "Margins"],
  ["cash", "Faster", "Collections"],
  ["cost", "Optimised", "Costs"],
  ["data", "AI-Driven", "Decisions"],
  ["enterprise", "A More Resilient", "Professional Services Business"],
];

function Icon({ type, size = 28 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (type) {
    case "market":
      return (
        <svg {...common}>
          <path d="M4 18V9" />
          <path d="M10 18V5" />
          <path d="M16 18v-7" />
          <path d="M3 20h18" />
        </svg>
      );

    case "contract":
      return (
        <svg {...common}>
          <path d="M6 3h9l3 3v15H6z" />
          <path d="M15 3v4h4" />
          <path d="M9 11h6M9 15h6M9 18h4" />
        </svg>
      );

    case "people":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M3.5 20c.5-4 2.5-6 5.5-6s5 2 5.5 6" />
          <path d="M14 14c3.2-.5 5.5 1.5 6 5" />
        </svg>
      );

    case "delivery":
      return (
        <svg {...common}>
          <path d="M4 5h16v14H4z" />
          <path d="M8 9h8M8 13h5M8 17h3" />
        </svg>
      );

    case "billing":
      return (
        <svg {...common}>
          <path d="M5 3h14v18l-3-2-4 2-4-2-3 2z" />
          <path d="M8 8h8M8 12h8M8 16h5" />
        </svg>
      );

    case "cash":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v10" />
          <path d="M15 9.5c-.7-.7-1.6-1-3-1-1.5 0-2.5.7-2.5 1.8 0 2.7 5.5 1.1 5.5 4 0 1.2-1.1 2-3 2-1.2 0-2.2-.3-3-1" />
        </svg>
      );

    case "revenue":
      return (
        <svg {...common}>
          <path d="M4 19V9" />
          <path d="M10 19V5" />
          <path d="M16 19v-7" />
          <path d="M3 19h18" />
        </svg>
      );

    case "cost":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v8M9 10h4a2 2 0 1 1 0 4H9" />
        </svg>
      );

    case "crm":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="3" />
          <path d="M5 20c.5-4 2.8-6 7-6s6.5 2 7 6" />
        </svg>
      );

    case "business":
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="14" rx="1" />
          <path d="M8 5V3h8v2M8 10h8M8 14h4" />
        </svg>
      );

    case "project":
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h16M4 18h16" />
          <circle cx="8" cy="6" r="1.5" />
          <circle cx="14" cy="12" r="1.5" />
          <circle cx="10" cy="18" r="1.5" />
        </svg>
      );

    case "expense":
      return (
        <svg {...common}>
          <rect x="5" y="3" width="14" height="18" />
          <path d="M8 7h8M8 11h8M8 15h4" />
        </svg>
      );

    case "integration":
      return (
        <svg {...common}>
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="7" r="3" />
          <circle cx="18" cy="17" r="3" />
          <path d="m8.7 10.8 6.5-2.7M8.7 13.2l6.5 2.7" />
        </svg>
      );

    case "data":
      return (
        <svg {...common}>
          <path d="M4 19V9M10 19V5M16 19v-7M22 19V3" />
          <path d="M3 19h20" />
        </svg>
      );

    case "enterprise":
      return (
        <svg {...common}>
          <path d="M4 20V8l8-4 8 4v12" />
          <path d="M8 20v-5h8v5" />
          <path d="M8 10h.01M12 10h.01M16 10h.01M8 13h.01M16 13h.01" />
        </svg>
      );

    case "utilisation":
      return (
        <svg {...common}>
          <path d="M5 19V9M12 19V5M19 19v-8" />
          <path d="M3 19h18" />
        </svg>
      );

    case "margin":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v10M15 9.5c-.7-.7-1.6-1-3-1-1.5 0-2.5.7-2.5 1.8 0 2.7 5.5 1.1 5.5 4 0 1.2-1.1 2-3 2-1.2 0-2.2-.3-3-1" />
        </svg>
      );

    case "chart":
      return (
        <svg {...common}>
          <path d="M4 19h16" />
          <path d="M7 19v-6M12 19V9M17 19v-10" />
          <path d="m5 10 4-4 3 3 6-6" />
        </svg>
      );

    case "database":
      return (
        <svg {...common}>
          <ellipse cx="12" cy="5" rx="8" ry="3" />
          <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
          <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
        </svg>
      );

    case "layers":
      return (
        <svg {...common}>
          <path d="M12 3 3 8l9 5 9-5-9-5z" />
          <path d="M3 13l9 5 9-5" />
          <path d="M3 17l9 5 9-5" />
        </svg>
      );

    default:
      return null;
  }
}

export default function ProfessionalServicesPage() {
  return (
    <main className="professional-page">

      {/* =====================================================
          HERO (redesigned — airlines layout)
      ====================================================== */}
      <section className="professional-hero">
        <div className="professional-hero-overlay" />

        <div className="professional-hero-inner">
          <div className="professional-hero-content">

            <h1>
              People to Possibilities.
              <br />
              From Skills to Cash.
            </h1>

            <h2>
              Win. Deliver. Grow. Profit. Sustain.
            </h2>

            <p>
              Connect people, skills, projects, finance and customers.
              Apply AI to improve utilisation, protect margin and
              accelerate cash.
            </p>

            <div className="professional-hero-actions">
              <Link href="/tellusyourproblem#tell-details" className="professional-primary-btn">
                Talk to Our Experts
                <span>→</span>
              </Link>

              <a href="#how-it-works" className="professional-secondary-btn">
                See How It Works
                <span className="professional-play-icon">▶</span>
              </a>
            </div>

            <div className="professional-hero-stats">
              {heroStats.map(([icon, line1, line2]) => (
                <div className="professional-hero-stat" key={line1}>
                  <div className="professional-hero-stat-icon">
                    <Icon type={icon} size={22} />
                  </div>
                  <span>
                    {line1}
                    <br />
                    {line2}
                  </span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* VALUE CHAIN */}
      <section className="professional-section" id="how-it-works">
        <div className="professional-container">

          <div className="section-heading">
            <span>CONNECTED BUSINESS VALUE</span>
            <h2>The End-to-End Professional Services Value Chain</h2>
            <p>
              Connected people. Projects. Profitability.
            </p>
          </div>

          <div className="value-chain-grid">
            {valueChain.map((item) => (
              <article
                className="value-chain-card"
                key={item.number}
                style={{ backgroundImage: `url(${item.image})` }}
                tabIndex={0}
              >
                <span className="value-chain-label">{item.title}</span>

                <div className="value-chain-reveal">
                  <p>{item.text}</p>
                  <span className="value-chain-arrow">→</span>
                </div>
              </article>
            ))}
          </div>

          <div className="lifecycle-bar">
            <strong>AI CONNECTS THE ENTIRE LIFECYCLE</strong>

            <span>
              Detect
              <b>|</b>
              Explain
              <b>|</b>
              Predict
              <b>|</b>
              Simulate
              <b>|</b>
              Recommend
              <b>|</b>
              Automate
            </span>
          </div>

        </div>
      </section>

      {/* PEOPLE TO MONEY */}
      <section className="professional-section professional-soft">
        <div className="professional-container">

          <div className="section-heading">
            <span>FROM PEOPLE TO PROFITABILITY</span>
            <h2>People to Money: How It All Connects</h2>
            <p>
              From employee cost to enterprise profitability.
            </p>
          </div>

          <div className="people-money-grid">
            {peopleToMoney.map((item, index) => (
              <article className="people-money-card" key={item.title}>

                <div className="people-money-top">
                  <div className="people-money-icon">
                    <Icon type={item.type} size={28} />
                  </div>

                  <div>
                    <strong>{item.title}</strong>

                    {item.subtitle && (
                      <span>{item.subtitle}</span>
                    )}
                  </div>
                </div>

                <ul>
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                {index !== peopleToMoney.length - 1 && (
                  <div className="people-money-arrow">→</div>
                )}

              </article>
            ))}
          </div>

          <div className="integrated-data-bar">
            <strong>ONE INTEGRATED DATA AND AI LAYER</strong>

            <span>
              Enterprise Data
              <b>|</b>
              Analytics
              <b>|</b>
              AI & Agents
              <b>|</b>
              Dashboards
              <b>|</b>
              Alerts
              <b>|</b>
              What-if
            </span>
          </div>

        </div>
      </section>

      {/* COLA */}
      <section className="professional-section">
        <div className="professional-container">

          <div className="section-heading">
            <span>PROFITABILITY CONTROL</span>
            <h2>COLA Intelligence</h2>
            <p>
              Protect margins. Stay ahead.
            </p>
          </div>

          <div className="cola-layout">

            <div className="cola-questions">
              {colaQuestions.map((question, index) => (
                <div className="cola-question" key={question}>
                  <span>?</span>
                  <p>{question}</p>
                </div>
              ))}
            </div>

            <div
              className="cola-insight"
              style={{ backgroundImage: `url(/assets/images/AI-INSIGHT.jpg)` }}
            >
              <div className="cola-insight-icon">
                <Icon type="data" size={30} />
              </div>

              <h3>AI INSIGHT</h3>

              <p>
                17 contracts reach COLA anniversary in the next
                90 days.
              </p>

              <p>
                5 have no rate revision initiated.
              </p>

              <strong>
                Projected annual margin erosion: ₹2.8 Cr.
              </strong>

              <Link href="/tellusyourproblem#tell-details">
                View COLA Radar
                <span>→</span>
              </Link>
            </div>

          </div>

        </div>
      </section>

      {/* AI USE CASES */}
      <section className="professional-section professional-soft">
        <div className="professional-container">

          <div className="section-heading">
            <span>FROM INSIGHT TO ACTION</span>
            <h2>AI Use Cases for Professional Services</h2>
            <p>
              From insight to action. Real business impact.
            </p>
          </div>

          <div className="ai-grid">
            {aiUseCases.map((item) => (
              <article className="ai-card" key={item.title}>

                <div className="ai-card-icon">
                  <Icon type={item.type} size={19} />
                </div>

                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>

                <span className="ai-card-arrow" aria-hidden="true">
                  →
                </span>

              </article>
            ))}
          </div>

        </div>
      </section>

      {/* BUSY BUT NOT MAKING MONEY */}
      <section className="professional-section" id="example">
        <div className="professional-container">

          <div className="section-heading">
            <span>PROFITABILITY QUESTION</span>
            <h2>We Are Busy. But Are We Making Money?</h2>
            <p>
              AI explains the gap between activity and profitability.
            </p>
          </div>

          <div className="profitability-layout">

            <div className="profitability-table">

              <div className="profit-column">
                <h3>
                  <span className="profit-column-icon">
                    <Icon type="chart" size={16} />
                  </span>
                  What's Happening?
                </h3>

                {whatsHappeningRows.map((row) => (
                  <div key={row.label}>
                    <span className="profit-row-label">
                      <Icon type={row.icon} size={16} />
                      {row.label}
                    </span>
                    <b className={`profit-trend profit-trend-${row.trend}`}>
                      {row.arrow}
                    </b>
                  </div>
                ))}
              </div>

              <div className="profit-arrow">
                →
              </div>

              <div className="profit-column result-column">
                <h3>
                  <span className="profit-column-icon">
                    <Icon type="chart" size={16} />
                  </span>
                  Result
                </h3>

                {resultRows.map((row) => (
                  <div key={row.label}>
                    <span className="profit-row-label">
                      <Icon type={row.icon} size={16} />
                      {row.label}
                    </span>
                    <b className={`profit-trend profit-trend-${row.trend}`}>
                      {row.arrow}
                    </b>
                  </div>
                ))}
              </div>

            </div>

            <div className="profit-arrow profit-arrow-ai">
              →
            </div>

            <div className="profitability-ai">
              <div className="profitability-ai-icon">
                <Icon type="data" size={32} />
              </div>

              <h3>AI Explains Why</h3>

              <p>
                Breaks down drivers, quantifies impact,
                simulates options and recommends actions.
              </p>

              <Link href="#example">
                See an Example
                <span>→</span>
              </Link>
            </div>

          </div>

        </div>
      </section>

      {/* QUESTIONS (redesigned — airlines split card: text + image) */}
      <section className="professional-section" id="questions">
        <div className="professional-container">

          <div className="questions-card">

            <div className="questions-content">

              <div className="section-heading">
                <span>REAL BUSINESS QUESTIONS</span>
                <h2>Key Questions We Help Answer</h2>
                <p>
                  Questions that matter to Partners, Finance,
                  Operations and Delivery leaders.
                </p>
              </div>

              <div className="questions-list">
                {busyQuestions.map((question, index) => (
                  <div className="questions-item" key={question}>
                    <span className="questions-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <p>{question}</p>

                    <span className="questions-arrow">→</span>
                  </div>
                ))}
              </div>

            </div>

            <div className="questions-image">
              <img
                src="/assets/images/Key-Questions-We-Help-Answer.jpg"
                alt="Professional Services"
              />
            </div>

          </div>

        </div>
      </section>

      {/* SYSTEMS */}
      <section className="professional-section">
        <div className="professional-container">

          <div className="section-heading">
            <span>ENTERPRISE DATA FOUNDATION</span>
            <h2>Enterprise Systems & Data</h2>
            <p>
              Bring your systems together. Add professional
              services context.
            </p>
          </div>

          <div className="systems-grid">
            {systems.map(([name, label, type], index) => (
              <div
                className={`system-card ${index === 0 ? "system-card-primary" : ""}`}
                key={name}
              >
                <div className="system-icon">
                  <Icon type={type} size={20} />
                </div>

                <strong>{name}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div className="data-boxes">

            <div className="data-box">
              <Icon type="database" size={24} />
              <div className="data-box-text">
                <strong>Unified Data</strong>
                <span>Enterprise systems + operational data</span>
              </div>
            </div>

            <div className="data-box">
              <Icon type="layers" size={24} />
              <div className="data-box-text">
                <strong>Professional Services Semantics</strong>
                <span>
                  People | Projects | Skills | Contracts | Utilisation |
                  Revenue | Margin | Cash
                </span>
              </div>
            </div>

            <div className="data-box">
              <Icon type="data" size={24} />
              <div className="data-box-text">
                <strong>AI & Analytics</strong>
                <span>
                  Prediction | Explanation | Simulation | Recommendation
                </span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* OUTCOMES (section just above CTA — hover redesigned) */}
      <section className="professional-section professional-soft">
        <div className="professional-container">

          <div className="section-heading">
            <span>BUSINESS IMPACT</span>
            <h2>Real Outcomes for Professional Services</h2>
            <p>
              Illustrative outcomes from connecting people,
              projects, finance and data.
            </p>
          </div>

          <div className="outcomes-grid">
            {outcomes.map((item) => (
              <article className="outcome-card" key={item.label}>
                <div
                  className="outcome-card-media"
                  style={{ backgroundImage: `url(${item.image})` }}
                />
                <div className="outcome-card-overlay" />

                <div className="outcome-card-body">
                  <div className="outcome-icon">
                    <Icon type={item.type} size={20} />
                  </div>

                  <strong>{item.value}</strong>
                  <span className="outcome-label">{item.label}</span>
                </div>

                <span className="outcome-arrow" aria-hidden="true">
                  →
                </span>
              </article>
            ))}
          </div>

          <div className="testimonial">
            <p>
              “Orane helped us connect our people, projects and
              finance. We now see the real economics of our
              business, and AI helps us act earlier.”
            </p>

            <span>
              — Partner, Global Professional Services Firm
            </span>
          </div>

        </div>
      </section>

      {/* =====================================================
          FINAL CTA (redesigned — airlines layout: text+button
          left, stats right, using existing outcomes data)
      ====================================================== */}
      <section className="professional-final">

        <div className="professional-final-overlay" />

        <div className="professional-container professional-final-inner">

          <div className="professional-final-text">
            <span>PROFESSIONAL SERVICES TRANSFORMATION</span>

            <h2>
              From People to Profitability.
              <br />
              Build a Smarter Professional Services Business.
            </h2>

            <p>
              Connect people, projects, customers, finance and AI
              around the decisions that matter.
            </p>

            <Link href="/tellusyourproblem#tell-details" className="professional-final-button">
              Let's Build a Smarter Business
              <span>→</span>
            </Link>
          </div>

          <div className="professional-final-stats">
            {outcomes.map((item) => (
              <div className="professional-final-stat" key={item.label}>
                <div className="professional-final-stat-icon">
                  <Icon type={item.type} size={20} />
                </div>
                <span>{item.label}</span>
              </div>
            ))}
          </div>

        </div>

      </section>

    </main>
  );
}